"use client";

import { useRef } from "react";
import Link from "next/link";
import Image from "next/image";

const materialsItems = [
  { label: "Granite", href: "/materials#granite" },
  { label: "Marble", href: "/materials#marble" },
  { label: "Quartz", href: "/materials#quartz" },
  { label: "Quartzite", href: "/materials#quartzite" },
  { label: "Edge Profiles", href: "/materials/edge-profiles" },
  { label: "Maintenance & Care", href: "/materials/maintenance" },
];

const companyItems = [
  { label: "About Us", href: "/about" },
  { label: "Our Team", href: "/contact#leadership" },
  { label: "Our Showroom", href: "/showroom" },
  { label: "Trade Partners", href: "/trade" },
];

const galleryItems = [
  { label: "Kitchen", href: "/gallery#kitchen" },
  { label: "Bathroom", href: "/gallery#bathroom" },
  { label: "Fireplace", href: "/gallery#fireplace" },
  { label: "Outdoor", href: "/gallery#outdoor" },
  { label: "Commercial", href: "/gallery#commercial" },
];

const navItems = [
  { label: "Company", href: "/about" },
  { label: "Materials", href: "/materials" },
  { label: "The Process", href: "/process" },
  { label: "Gallery", href: "/gallery" },
  { label: "Insights", href: "/insights" },
  { label: "Contact", href: "/contact" },
] as const;

function NavDropdown({ label, href, items }: { label: string; href: string; items: { label: string; href: string }[] }) {
  return (
    <div className="group relative flex items-center h-full">
      <Link href={href} className="transition-colors hover:text-brand-blue py-6">
        {label}
      </Link>
      <div className="absolute left-1/2 -translate-x-1/2 top-[80%] pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 ease-out z-[100]">
        <div className="bg-white rounded-xl shadow-xl w-64 py-3 border border-black/5 overflow-hidden">
          <div className="flex flex-col">
            {items.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="block px-6 py-3 text-xs font-semibold !text-black hover:bg-brand-blue/5 hover:text-brand-blue transition-colors text-left normal-case tracking-wide"
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SiteHeader() {
  const menuRef = useRef<HTMLDetailsElement>(null);

  const closeMenu = () => {
    if (menuRef.current) {
      menuRef.current.open = false;
    }
  };

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-iron-slate/95 text-clarity-white backdrop-blur">
      <div className="flex items-center px-6 py-4">
        <Link href="/" className="flex-shrink-0">
          <Image
            src="/images/logo.webp"
            alt="Integrity Kitchen Solutions"
            width={180}
            height={45}
            className="h-10 w-auto"
            style={{ width: "auto" }}
            priority
            unoptimized
          />
          <span className="sr-only">IKS - Integrity Kitchen Solutions</span>
        </Link>
        <div className="relative mx-auto flex max-w-7xl flex-1 items-center justify-center">
          <div className="hidden items-center gap-8 lg:flex">
            <nav className="flex items-center gap-8 text-sm font-medium uppercase tracking-[0.24em]">
              <NavDropdown
                label="Company"
                href="/about"
                items={companyItems}
              />
              <NavDropdown
                label="Materials"
                href="/materials"
                items={materialsItems}
              />
              <Link href="/process" className="transition-colors hover:text-brand-blue">
                The Process
              </Link>
              <NavDropdown
                label="Gallery"
                href="/gallery"
                items={galleryItems}
              />
              <Link href="/insights" className="transition-colors hover:text-brand-blue">
                Insights
              </Link>
              <Link href="/contact" className="transition-colors hover:text-brand-blue">
                Contact
              </Link>
            </nav>
            <Link
              href="/contact"
              className="rounded-full bg-brand-blue px-5 py-2.5 text-sm font-semibold uppercase tracking-[0.2em] text-clarity-white transition hover:bg-brand-blue/90 hover:text-clarity-white"
            >
              Request a Quote
            </Link>
          </div>
          <details ref={menuRef} className="absolute right-0 top-1/2 -translate-y-1/2 lg:hidden">
            <summary className="flex items-center justify-center rounded-full border border-clarity-white/30 p-2">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
              </svg>
            </summary>
            <div className="absolute right-0 mt-3 w-64 rounded-2xl border border-clarity-white/20 bg-iron-slate/95 p-4 shadow-[0_24px_60px_-30px_rgba(0,0,0,0.5)]">
              <nav className="flex flex-col gap-3 text-sm font-medium uppercase tracking-[0.2em]">
                {navItems.map((item) => (
                  <Link key={item.href} href={item.href} onClick={closeMenu} className="block rounded-lg px-3 py-2 hover:bg-brand-blue/12">
                    {item.label}
                  </Link>
                ))}
              </nav>
              <div className="mt-4">
                <Link
                  href="/contact"
                  onClick={closeMenu}
                  className="block rounded-full bg-brand-blue px-4 py-2 text-center text-sm font-semibold uppercase tracking-[0.2em] text-clarity-white hover:text-clarity-white"
                >
                  Request a Quote
                </Link>
              </div>
            </div>
          </details>
        </div>
      </div>
    </header>
  );
}
